import {
    Component,
    computed,
    inject,
    input,
    InputSignal,
    OnDestroy,
    OnInit,
    Signal,
    signal,
    WritableSignal,
} from '@angular/core';
import { BackgroundColorDirective, ButtonDirective, TypographyDirective } from '@dartcounter/ui';
import { OverTheAirUpdateApiService } from '@dc-api/over-the-air-update.api.service';
import { SmartDeviceFirmware } from '@dc-core/dc-backend/dc-classes';
import { SmartDeviceType } from '@dc-core/dc-backend/dc-enums';
import { SelectItem } from '@dc-core/dc-statistics/statistics.globals';
import { Platform } from '@ionic/angular/standalone';
import { Subscription } from 'rxjs';
import { AppSelectComponent } from 'src/app/shared/components/select/select.component';
import { CenterDialogLayoutComponent } from 'src/app/shared/dialogs/center-dialog-layout/center-dialog-layout.component';
import { EnsureImplementsAllFields } from 'src/app/shared/services/modal.service';
import { ModalService } from 'src/app/shared/services/modal.service';

export interface SmartDevicesChangeFirmwarePayload {
    smartDeviceType: InputSignal<SmartDeviceType>;
}

@Component({
    selector: 'app-smart-devices-change-firmware',
    standalone: true,
    imports: [
        AppSelectComponent,
        CenterDialogLayoutComponent,
        ButtonDirective,
        BackgroundColorDirective,
        TypographyDirective,
    ],
    templateUrl: './smart-devices-change-firmware.component.html',
    styleUrl: './smart-devices-change-firmware.component.css',
})
export class SmartDevicesChangeFirmwareComponent
    implements EnsureImplementsAllFields<SmartDevicesChangeFirmwarePayload>, OnInit, OnDestroy
{
    public smartDeviceType: InputSignal<SmartDeviceType> = input.required();

    private overTheAirUpdateApiService: OverTheAirUpdateApiService = inject(OverTheAirUpdateApiService);
    private platform: Platform = inject(Platform);
    private modalService: ModalService = inject(ModalService);

    public firmwareOptions: Signal<SelectItem[]> = computed(() =>
        this.firmwares()?.map((firmware) => ({ label: firmware.mender_key, value: firmware.id.toString() }))
    );

    public selectedFirmware: WritableSignal<SmartDeviceFirmware> = signal(null);
    private firmwares: WritableSignal<SmartDeviceFirmware[]> = signal(null);
    private backButtonSubscription: Subscription;

    public ngOnInit(): void {
        this.getFirmwares(this.smartDeviceType());

        this.backButtonSubscription = this.platform.backButton.subscribeWithPriority(9999, () => {
            this.dismiss();
        });
    }

    public changeFirmware(selectedFirmwareId: string): void {
        this.selectedFirmware.set(this.firmwares().find((firmware) => firmware.id === Number(selectedFirmwareId)));
    }

    public patch(): void {
        this.dismiss(this.selectedFirmware());
    }

    public dismiss(firmware?: SmartDeviceFirmware): void {
        this.modalService.dismiss(firmware);
    }

    public ngOnDestroy(): void {
        if (this.backButtonSubscription) {
            this.backButtonSubscription.unsubscribe();
        }
    }

    private getFirmwares(type: SmartDeviceType): void {
        this.overTheAirUpdateApiService
            .getSmartDeviceFirmwares({ type })
            .then((res) => this.firmwares.set(res.data as SmartDeviceFirmware[]));
    }
}

